import { useCallback, useEffect, useMemo } from 'react'

import { useFeatureFlag } from '../../../../../../app/_providers/ExperimentationProvider.client'
import { usePortal } from '../../../../../../app/_providers/PortalProvider.client'
import { useTranslation } from '../../../../../../app/_providers/TranslationProvider.client'
import { EmbroideryService } from '../../../../../../containers/EmbroideryService'
import { useBag } from '../../../../../../lib/hooks/useBag'
import { ProductDetailsContainer } from '../ProductDetailsContainer'

/**
 * @deprecated Use useCart, VariantSelectionForm, useCartDialog, and possible some new Embroidery utilities if
 */
export const useAddToBag = () => {
  const { hasMandatoryEmbroidery } = usePortal()

  const {
    featuredProduct,
    productGroup,
    isPortalColorSelected,
    isProductEmbroiderable,
    isProductFinalSale,
    isProductOnWaitlist,
    selectedColorKey,
    selectedSize,
    selectedVariant,
    sizes,
  } = ProductDetailsContainer.useContainer()

  const {
    detailsSection: {
      addToBagCTA,
      selectSizeAddToBagButtonCTA,
      inFlightAddToBagCTA,
      waitlistAddToBagCTA,
    },
  } = useTranslation()

  const { selectedEmbroideryFormOptions } = EmbroideryService.useContainer()

  const {
    addToBag,
    addToBagErrors,
    handleAddToBagClick,
    hasSizeErrors,
    isAddToCartInFlight,
    quantity: quantityVal,
    resetInvalidBagClickStatus,
    setQuantity,
  } = useBag({
    isProductFinalSale,
    isProductWaitlistable: isProductOnWaitlist,
    requiresSizeSelection: sizes.length > 0,
    selectedVariant,
    selectedSize,
    isPortalColorSelected,
    isProductEmbroiderable,
  })

  const addToBagCtaCopyTestDecision = useFeatureFlag<
    'atb-cta-copy-test-control' | 'atb-cta-copy-test-variant'
  >({
    key: 'atb-cta-copy-test',
    controlVariant: 'atb-cta-copy-test-control',
    ifAccessedPriorToDecisionInitialization: 'lock-pageview-to-controlVariant',
    skip: isProductOnWaitlist,
  })

  const isSelectSizeCTACopyVariant = addToBagCtaCopyTestDecision === 'atb-cta-copy-test-variant'
  const shouldShowSelectSizeCta = isSelectSizeCTACopyVariant && hasSizeErrors

  const quantity = useMemo(() => quantityVal.toString(), [quantityVal])

  const handleChangeQuantity = useCallback((q: string) => setQuantity(parseInt(q)), [setQuantity])

  const { isSizeErrored, isAddToBagDisabled } = useMemo(() => {
    const isSizeErrored: boolean = !selectedSize && addToBagErrors.missingRequiredSize
    const isAddToBagDisabled =
      addToBagErrors.missingRequiredTextEmbroidery ||
      isSizeErrored ||
      (!!selectedSize?.soldOut && !isProductOnWaitlist) ||
      (featuredProduct.soldOut && !isProductOnWaitlist) ||
      isAddToCartInFlight
    return { isAddToBagDisabled, isSizeErrored }
  }, [
    addToBagErrors.missingRequiredSize,
    addToBagErrors.missingRequiredTextEmbroidery,
    featuredProduct.soldOut,
    isAddToCartInFlight,
    isProductOnWaitlist,
    selectedSize,
  ])

  const addToBagCta = useMemo(() => {
    if (isAddToCartInFlight) return inFlightAddToBagCTA
    if (isProductOnWaitlist) return waitlistAddToBagCTA
    if (shouldShowSelectSizeCta) return selectSizeAddToBagButtonCTA
    return addToBagCTA
  }, [
    addToBagCTA,
    inFlightAddToBagCTA,
    isAddToCartInFlight,
    isProductOnWaitlist,
    selectSizeAddToBagButtonCTA,
    shouldShowSelectSizeCta,
    waitlistAddToBagCTA,
  ])

  const hasMandatoryTextAndLogoEmbroidery = hasMandatoryEmbroidery({
    categoryAnalyticsId: productGroup.rawCategory ?? productGroup.category,
  })

  /**
   * Reset bag errors if:
   * - Selected color or size is changed
   * - A portal with mandatory text & logo is selected, and the text embroidery changes
   */
  useEffect(resetInvalidBagClickStatus, [
    resetInvalidBagClickStatus,
    selectedColorKey,
    selectedSize,
  ])

  useEffect(() => {
    if (isPortalColorSelected && hasMandatoryTextAndLogoEmbroidery) resetInvalidBagClickStatus()
  }, [
    selectedEmbroideryFormOptions.textFirstLine,
    isPortalColorSelected,
    hasMandatoryTextAndLogoEmbroidery,
    resetInvalidBagClickStatus,
  ])

  return {
    addToBag,
    addToBagCta,
    addToBagErrors,
    isAddToBagDisabled,
    isAddToCartInFlight,
    isSizeErrored,
    onAddToBagClick: handleAddToBagClick,
    onChangeQuantity: handleChangeQuantity,
    quantity,
    resetInvalidBagClickStatus,
    shouldShowSelectSizeCta,
  }
}
